import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';

export interface AwningPricing {
  id: string;
  model_id: string;
  size_display_ft: string;
  size_display_m: string;
  retail_price: number;
  install_cost: number;
  total_price: number;
  financing_monthly: number;
  cost_price: number;
  is_active: boolean;
}

export interface AwningModel {
  id: string;
  model_id: string;
  name: string;
  base_price: number;
  description: string;
  tagline: string;
  badge?: string;
  bullets: string[];
  best_for: string;
  includes: string[];
  warranty_years: number;
  awning_type: string;
  smart_home_compatible: boolean;
  wind_sensor: boolean;
  active: boolean;
}

export interface AwningAddon {
  id: string;
  addon_id: string;
  name: string;
  price: number;
  has_quantity: boolean;
  model_compatibility: string[] | null;
  description: string;
  active: boolean;
}

export interface AwningPricingData {
  pricingByModel: Record<string, AwningPricing[]>;
  models: AwningModel[];
  addons: AwningAddon[];
  isLoading: boolean;
  error: string | null;
}

export const useAwningPricing = (): AwningPricingData => {
  const [pricingByModel, setPricingByModel] = useState<Record<string, AwningPricing[]>>({});
  const [models, setModels] = useState<AwningModel[]>([]);
  const [addons, setAddons] = useState<AwningAddon[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAwningData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch awning pricing data
        const { data: pricingData, error: pricingError } = await supabase
          .from('awning_pricing')
          .select('*')
          .eq('is_active', true)
          .order('size_ft_width', { ascending: true })
          .order('size_ft_projection', { ascending: true });

        if (pricingError) {
          throw new Error(`Failed to fetch awning pricing: ${pricingError.message}`);
        }

        // Fetch awning models
        const { data: modelsData, error: modelsError } = await supabase
          .from('awning_models')
          .select('*')
          .eq('active', true)
          .order('base_price', { ascending: true });

        if (modelsError) {
          throw new Error(`Failed to fetch awning models: ${modelsError.message}`);
        }

        // Fetch awning addons
        const { data: addonsData, error: addonsError } = await supabase
          .from('awning_addons')
          .select('*')
          .eq('active', true)
          .order('name', { ascending: true });

        if (addonsError) {
          throw new Error(`Failed to fetch awning addons: ${addonsError.message}`);
        }

        // Group pricing by model
        const pricingLookup: Record<string, AwningPricing[]> = {};
        if (pricingData) {
          pricingData.forEach((item: any) => {
            if (!pricingLookup[item.model_id]) {
              pricingLookup[item.model_id] = [];
            }
            pricingLookup[item.model_id].push({
              id: item.id,
              model_id: item.model_id,
              size_display_ft: item.size_display_ft,
              size_display_m: item.size_display_m,
              retail_price: parseFloat(item.retail_price) || 0,
              install_cost: parseFloat(item.install_cost) || 0,
              total_price: parseFloat(item.total_price) || 0,
              financing_monthly: parseFloat(item.financing_monthly) || 0,
              cost_price: parseFloat(item.cost_price) || 0,
              is_active: item.is_active,
            });
          });
        }

        setPricingByModel(pricingLookup);
        setModels(modelsData || []);
        setAddons(addonsData || []);
      } catch (err) {
        console.error('Error fetching awning data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');

        // Fallback to empty data if database fetch fails
        setPricingByModel({});
        setModels([]);
        setAddons([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAwningData();
  }, []);

  return {
    pricingByModel,
    models,
    addons,
    isLoading,
    error,
  };
};
